// YERİ: src/app/domains/website/core/mkt-chrome-text.ts
//
// VİTRİN KABUK METİNLERİ — CMS'in sağlamadığı, koda ait birkaç dize.
//
// ── NEDEN TRANSLOCO DEĞİL ──────────────────────────────────────────────────
// Vitrinin dili portal kullanıcısının dili DEĞİL, ziyaretçinin gezdiği
// `/:lang` rotasıdır (LanguageStore). Transloco portal oturumuna bağlı; burada
// kullanılsaydı İngilizce vitrini gezen ziyaretçi, portalı Türkçe kullanan
// birinin tarayıcısında Türkçe kabuk görürdü.
//
// ── NEDEN CMS DEĞİL ────────────────────────────────────────────────────────
// Bunlar içerik değil: ekran okuyucu etiketi, boş medya yer tutucusu, CMS
// etiketi gelmediğinde düşülen yedek. Kiracının düzenlemesi gereken şeyler
// olmadığı için veritabanına konmadı.

import { computed, inject, Injectable, Signal } from '@angular/core';
import { LanguageStore } from './language.store';

type Sozluk = Record<string, string>;

/** Anahtar → dil → metin. Dil yoksa İngilizceye düşer (vitrinin yayın dili). */
const TEXT: Record<string, Sozluk> = {
  menu: {
    en: 'Menu', tr: 'Menü', de: 'Menü', fr: 'Menu', it: 'Menu', es: 'Menú',
    pt: 'Menu', ru: 'Меню', fa: 'منو', ar: 'القائمة',
    ko: '메뉴', vi: 'Menu', ja: 'メニュー', zh: '菜单', 'zh-tw': '選單', th: 'เมนู',
  },
  effectiveDate: {
    en: 'Effective date', tr: 'Yürürlük tarihi', de: 'Gültig ab',
    fr: "Date d'entrée en vigueur", it: 'Data di efficacia',
    es: 'Fecha de entrada en vigor', pt: 'Data de entrada em vigor',
    ru: 'Дата вступления в силу', fa: 'تاریخ اجرا', ar: 'تاريخ السريان',
    ko: '시행일', vi: 'Ngày hiệu lực', ja: '施行日', zh: '生效日期',
    'zh-tw': '生效日期', th: 'วันที่มีผลบังคับใช้',
  },
  mediaSlot: {
    en: 'Media slot', tr: 'Medya yuvası', de: 'Medienplatz',
    fr: 'Emplacement média', it: 'Spazio media', es: 'Espacio multimedia',
    pt: 'Espaço multimédia', ru: 'Место для медиа', fa: 'جایگاه رسانه',
    ar: 'موضع الوسائط',
    ko: '미디어 영역', vi: 'Vị trí đa phương tiện', ja: 'メディア枠',
    zh: '媒体位', 'zh-tw': '媒體區塊', th: 'พื้นที่สื่อ',
  },
  video: {
    en: 'Video', tr: 'Video', de: 'Video', fr: 'Vidéo', it: 'Video',
    es: 'Vídeo', pt: 'Vídeo', ru: 'Видео', fa: 'ویدیو', ar: 'فيديو',
    ko: '동영상', vi: 'Video', ja: '動画', zh: '视频', 'zh-tw': '影片', th: 'วิดีโอ',
  },
  playVideo: {
    en: 'Play video', tr: 'Videoyu oynat', de: 'Video abspielen',
    fr: 'Lire la vidéo', it: 'Riproduci il video', es: 'Reproducir el vídeo',
    pt: 'Reproduzir o vídeo', ru: 'Воспроизвести видео',
    fa: 'پخش ویدیو', ar: 'تشغيل الفيديو',
    ko: '동영상 재생', vi: 'Phát video', ja: '動画を再生', zh: '播放视频',
    'zh-tw': '播放影片', th: 'เล่นวิดีโอ',
  },
  // Free trial hint next to sign-up CTAs ({n} = days). Shown only when the
  // platform publishes a general trial offer (GET /api/public/trial-offer).
  trialHint: {
    en: 'Try free for {n} days', tr: '{n} gün ücretsiz deneyin',
    de: '{n} Tage kostenlos testen', fr: 'Essai gratuit de {n} jours',
    it: 'Prova gratis per {n} giorni', es: 'Pruébalo gratis durante {n} días',
    pt: 'Experimente grátis por {n} dias', ru: 'Попробуйте бесплатно {n} дней',
    fa: '{n} روز رایگان امتحان کنید', ar: 'جرّب مجانًا لمدة {n} يومًا',
    ko: '{n}일 무료 체험', vi: 'Dùng thử miễn phí {n} ngày', ja: '{n}日間無料でお試し',
    zh: '免费试用 {n} 天', 'zh-tw': '免費試用 {n} 天', th: 'ทดลองใช้ฟรี {n} วัน',
  },
  trialNoCard: {
    en: 'no credit card required', tr: 'kredi kartı gerekmez',
    de: 'keine Kreditkarte erforderlich', fr: 'sans carte bancaire',
    it: 'nessuna carta di credito richiesta', es: 'sin tarjeta de crédito',
    pt: 'sem cartão de crédito', ru: 'без банковской карты',
    fa: 'بدون نیاز به کارت اعتباری', ar: 'دون الحاجة إلى بطاقة ائتمان',
    ko: '신용카드 불필요', vi: 'không cần thẻ tín dụng', ja: 'クレジットカード不要',
    zh: '无需信用卡', 'zh-tw': '無需信用卡', th: 'ไม่ต้องใช้บัตรเครดิต',
  },
  // Legal document header ({v} = version, {d} = already-formatted date).
  legalMeta: {
    en: 'Version {v} · Last updated {d}', tr: 'Sürüm {v} · Son güncelleme {d}',
    de: 'Version {v} · Zuletzt aktualisiert {d}',
    fr: 'Version {v} · Dernière mise à jour {d}',
    it: 'Versione {v} · Ultimo aggiornamento {d}',
    es: 'Versión {v} · Última actualización {d}',
    pt: 'Versão {v} · Última atualização {d}',
    ru: 'Версия {v} · Последнее обновление {d}',
    fa: 'نسخهٔ {v} · آخرین به‌روزرسانی {d}',
    ar: 'الإصدار {v} · آخر تحديث {d}',
    ko: '버전 {v} · 최종 업데이트 {d}',
    vi: 'Phiên bản {v} · Cập nhật lần cuối {d}',
    ja: 'バージョン {v} · 最終更新 {d}',
    zh: '版本 {v} · 最后更新 {d}',
    'zh-tw': '版本 {v} · 最後更新 {d}',
    th: 'เวอร์ชัน {v} · อัปเดตล่าสุด {d}',
  },
  // Shown when the document has no translation yet ({l} = language code).
  legalFallback: {
    en: 'This document is not available in your language yet and is shown in {l}.',
    tr: 'Bu belge henüz sizin dilinizde yok; {l} dilinde gösteriliyor.',
    de: 'Dieses Dokument liegt noch nicht in Ihrer Sprache vor und wird auf {l} angezeigt.',
    fr: "Ce document n'est pas encore disponible dans votre langue et s'affiche en {l}.",
    it: 'Questo documento non è ancora disponibile nella tua lingua ed è mostrato in {l}.',
    es: 'Este documento aún no está disponible en su idioma y se muestra en {l}.',
    pt: 'Este documento ainda não está disponível no seu idioma e é apresentado em {l}.',
    ru: 'Этот документ пока не переведён на ваш язык и показан на {l}.',
    fa: 'این سند هنوز به زبان شما موجود نیست و به {l} نمایش داده می‌شود.',
    ar: 'هذه الوثيقة غير متوفرة بلغتك بعد وتُعرض بـ {l}.',
    ko: '이 문서는 아직 귀하의 언어로 제공되지 않아 {l}(으)로 표시됩니다.',
    vi: 'Tài liệu này chưa có bằng ngôn ngữ của bạn và đang hiển thị bằng {l}.',
    ja: 'この文書はまだお使いの言語に対応していないため、{l}で表示しています。',
    zh: '本文档尚无您所用语言的版本，现以 {l} 显示。',
    'zh-tw': '本文件尚無您所用語言的版本，目前以 {l} 顯示。',
    th: 'เอกสารนี้ยังไม่มีในภาษาของคุณ จึงแสดงเป็นภาษา {l}',
  },
  // Cookie consent banner (mkt-consent-banner). `en-ca` falls back to `en`.
  consentLabel: {
    en: 'Cookie consent', tr: 'Çerez onayı', de: 'Cookie-Einwilligung',
    fr: 'Consentement aux cookies', it: 'Consenso ai cookie',
    es: 'Consentimiento de cookies', pt: 'Consentimento de cookies',
    ru: 'Согласие на использование cookie', fa: 'رضایت کوکی',
    ar: 'الموافقة على ملفات تعريف الارتباط',
    ko: '쿠키 동의', vi: 'Đồng ý cookie', ja: 'Cookie の同意', zh: 'Cookie 同意',
    'zh-tw': 'Cookie 同意', th: 'การยินยอมใช้คุกกี้',
  },
  consentText: {
    en: 'We use cookies to measure site traffic and improve our ads. Necessary cookies are always on; analytics and advertising cookies are used only with your permission.',
    tr: 'Site trafiğini ölçmek ve reklamlarımızı iyileştirmek için çerez kullanıyoruz. Zorunlu çerezler her zaman açıktır; analiz ve reklam çerezleri yalnızca izninizle kullanılır.',
    de: 'Wir verwenden Cookies, um den Website-Verkehr zu messen und unsere Werbung zu verbessern. Notwendige Cookies sind immer aktiv; Analyse- und Werbe-Cookies setzen wir nur mit Ihrer Zustimmung ein.',
    fr: "Nous utilisons des cookies pour mesurer la fréquentation du site et améliorer nos publicités. Les cookies nécessaires sont toujours actifs ; les cookies d'analyse et publicitaires ne sont utilisés qu'avec votre accord.",
    it: 'Utilizziamo i cookie per misurare il traffico del sito e migliorare i nostri annunci. I cookie necessari sono sempre attivi; i cookie analitici e pubblicitari vengono usati solo con il tuo consenso.',
    es: 'Usamos cookies para medir el tráfico del sitio y mejorar nuestros anuncios. Las cookies necesarias están siempre activas; las de análisis y publicidad solo se usan con su permiso.',
    pt: 'Utilizamos cookies para medir o tráfego do site e melhorar os nossos anúncios. Os cookies necessários estão sempre ativos; os de análise e publicidade só são usados com a sua autorização.',
    ru: 'Мы используем файлы cookie, чтобы измерять посещаемость сайта и улучшать рекламу. Необходимые cookie включены всегда; аналитические и рекламные — только с вашего согласия.',
    fa: 'ما برای سنجش ترافیک سایت و بهبود تبلیغات از کوکی استفاده می‌کنیم. کوکی‌های ضروری همیشه فعال‌اند؛ کوکی‌های تحلیلی و تبلیغاتی فقط با اجازهٔ شما به کار می‌روند.',
    ar: 'نستخدم ملفات تعريف الارتباط لقياس زيارات الموقع وتحسين إعلاناتنا. ملفات تعريف الارتباط الضرورية مفعّلة دائمًا؛ أما ملفات التحليل والإعلان فلا تُستخدم إلا بإذنك.',
    ko: '사이트 트래픽을 측정하고 광고를 개선하기 위해 쿠키를 사용합니다. 필수 쿠키는 항상 사용되며, 분석 및 광고 쿠키는 동의하신 경우에만 사용됩니다.',
    vi: 'Chúng tôi dùng cookie để đo lưu lượng truy cập và cải thiện quảng cáo. Cookie cần thiết luôn được bật; cookie phân tích và quảng cáo chỉ được dùng khi bạn cho phép.',
    ja: 'サイトのアクセス数の測定と広告の改善のために Cookie を使用しています。必須 Cookie は常に有効です。分析および広告 Cookie は、同意いただいた場合にのみ使用します。',
    zh: '我们使用 Cookie 来统计网站流量并改进广告。必要 Cookie 始终启用；分析和广告 Cookie 仅在您许可后使用。',
    'zh-tw': '我們使用 Cookie 來統計網站流量並改善廣告。必要 Cookie 一律啟用；分析與廣告 Cookie 僅在您同意後使用。',
    th: 'เราใช้คุกกี้เพื่อวัดปริมาณการเข้าชมเว็บไซต์และปรับปรุงโฆษณา คุกกี้ที่จำเป็นจะเปิดใช้งานเสมอ ส่วนคุกกี้เพื่อการวิเคราะห์และโฆษณาจะใช้เมื่อได้รับอนุญาตจากคุณเท่านั้น',
  },
  consentAccept: {
    en: 'Accept all', tr: 'Tümünü kabul et', de: 'Alle akzeptieren',
    fr: 'Tout accepter', it: 'Accetta tutti', es: 'Aceptar todo',
    pt: 'Aceitar tudo', ru: 'Принять все', fa: 'پذیرش همه', ar: 'قبول الكل',
    ko: '모두 허용', vi: 'Chấp nhận tất cả', ja: 'すべて許可', zh: '全部接受',
    'zh-tw': '全部接受', th: 'ยอมรับทั้งหมด',
  },
  consentReject: {
    en: 'Only necessary', tr: 'Yalnızca zorunlu', de: 'Nur notwendige',
    fr: 'Uniquement nécessaires', it: 'Solo necessari', es: 'Solo necesarias',
    pt: 'Apenas necessários', ru: 'Только необходимые', fa: 'فقط ضروری',
    ar: 'الضرورية فقط',
    ko: '필수만 허용', vi: 'Chỉ cần thiết', ja: '必須のみ', zh: '仅必要',
    'zh-tw': '僅限必要', th: 'เฉพาะที่จำเป็น',
  },
  consentLearnMore: {
    en: 'Cookie policy', tr: 'Çerez politikası', de: 'Cookie-Richtlinie',
    fr: 'Politique relative aux cookies', it: 'Informativa sui cookie',
    es: 'Política de cookies', pt: 'Política de cookies',
    ru: 'Политика в отношении cookie', fa: 'سیاست کوکی',
    ar: 'سياسة ملفات تعريف الارتباط',
    ko: '쿠키 정책', vi: 'Chính sách cookie', ja: 'Cookie ポリシー', zh: 'Cookie 政策',
    'zh-tw': 'Cookie 政策', th: 'นโยบายคุกกี้',
  },
  // Footer link that reopens the consent banner.
  consentPreferences: {
    en: 'Cookie preferences', tr: 'Çerez tercihleri', de: 'Cookie-Einstellungen',
    fr: 'Préférences en matière de cookies', it: 'Preferenze cookie',
    es: 'Preferencias de cookies', pt: 'Preferências de cookies',
    ru: 'Настройки cookie', fa: 'تنظیمات کوکی',
    ar: 'تفضيلات ملفات تعريف الارتباط',
    ko: '쿠키 설정', vi: 'Tùy chọn cookie', ja: 'Cookie の設定', zh: 'Cookie 偏好设置',
    'zh-tw': 'Cookie 偏好設定', th: 'การตั้งค่าคุกกี้',
  },
};

@Injectable({ providedIn: 'root' })
export class MktChromeText {
  private lang = inject(LanguageStore);

  /**
   * Aktif vitrin dilinde metin.
   * Bilinmeyen dil ya da bilinmeyen anahtar → İngilizce; o da yoksa anahtarın
   * kendisi (sessizce boş dönmek, eksikliği görünmez yapardı).
   */
  t(key: string): string {
    const sozluk = TEXT[key];
    if (!sozluk) return key;
    // Full code first ('zh-tw' is its own script), then the base language
    // ('en-ca' → 'en').
    const tam = (this.lang.current() || 'en').toLowerCase();
    return sozluk[tam] ?? sozluk[tam.split('-')[0]] ?? sozluk['en'] ?? key;
  }

  /** Şablonda tekrar tekrar çağırmamak için hazır sinyal. */
  sinyal(key: string): Signal<string> {
    return computed(() => this.t(key));
  }
}
